import { motion } from "framer-motion";
import { Container, FadeIn } from "@/components/Section";
import CinematicImage from "@/components/CinematicImage";
import { TOK_PHILOSOPHY, TOK_PHILOSOPHY_BY_KEY } from "@/lib/philosophy";

/**
 * PhilosophyQuote — cinematic, spiritual, minimal.
 *
 * Pulls from the TOK philosophy library so attribution + tone stay
 * consistent across surfaces. Pass `lineKey` to pin a specific line,
 * or omit to use the core "awaken the village" statement.
 *
 * Variants:
 *   - hero     → large editorial centred quote (homepage / manifestos)
 *   - whisper  → small, low-key, used inside other sections or footers
 *   - banner   → full-width cinematic band with subtle amber glow
 *
 * Optional atmospheric background:
 *   bgImage         — desaturated image layered behind the quote
 *   bgFocalMobile   — focal point on mobile (default "50% 35%")
 *   bgFocalDesktop  — focal point on desktop (default "center")
 *   bgOpacity       — image opacity 0-1 (default 0.22)
 */
export default function PhilosophyQuote({
  lineKey,
  variant = "hero",
  className = "",
  testId = "philosophy-quote",
  showAttribution = true,
  bgImage,
  bgFocalMobile = "50% 35%",
  bgFocalDesktop = "center",
  bgOpacity = 0.22,
}) {
  const entry = lineKey ? TOK_PHILOSOPHY_BY_KEY[lineKey] || TOK_PHILOSOPHY.core : TOK_PHILOSOPHY.core;
  const { line, attribution } = entry;

  if (variant === "whisper") {
    return (
      <p
        data-testid={testId}
        className={`text-[11px] sm:text-xs uppercase tracking-[0.35em] text-amber-400/70 font-bold italic ${className}`}
      >
        &ldquo;{line}&rdquo;
        {showAttribution && (
          <span className="ml-2 not-italic text-zinc-500 normal-case tracking-[0.2em]">
            — {attribution}
          </span>
        )}
      </p>
    );
  }

  if (variant === "banner") {
    return (
      <section
        data-testid={testId}
        className={`relative bg-black border-y border-amber-500/10 overflow-hidden ${className}`}
      >
        {bgImage && (
          <>
            <div className="absolute inset-0" style={{ opacity: bgOpacity }}>
              <CinematicImage
                src={bgImage}
                mobile={bgFocalMobile}
                desktop={bgFocalDesktop}
                scale={1.04}
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/55 to-black" />
          </>
        )}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-72 w-[40rem] rounded-full bg-amber-400/[0.06] blur-[120px]" />
        <Container className="relative py-20 sm:py-24 lg:py-28">
          <FadeIn>
            <motion.blockquote
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="max-w-4xl"
            >
              <p className="font-display font-black uppercase italic leading-[1.05] tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
                &ldquo;{line}&rdquo;
              </p>
              {showAttribution && (
                <footer className="mt-7 text-[11px] uppercase tracking-[0.4em] text-amber-400 font-bold">
                  — {attribution}
                </footer>
              )}
            </motion.blockquote>
          </FadeIn>
        </Container>
      </section>
    );
  }

  // hero — centred editorial moment
  return (
    <section
      data-testid={testId}
      className={`relative py-24 sm:py-32 border-t border-amber-500/10 overflow-hidden ${className}`}
    >
      {bgImage && (
        <>
          <div className="absolute inset-0" style={{ opacity: bgOpacity }}>
            <CinematicImage
              src={bgImage}
              mobile={bgFocalMobile}
              desktop={bgFocalDesktop}
              scale={1.06}
            />
          </div>
          {/* Cinematic vignette so the quote stays legible over the artwork */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.25)_0%,rgba(0,0,0,0.75)_55%,rgba(0,0,0,0.95)_100%)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
        </>
      )}
      <Container className="relative">
        <FadeIn>
          <blockquote className="max-w-4xl mx-auto text-center">
            <p className="font-display font-black uppercase italic text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-white">
              &ldquo;{line}&rdquo;
            </p>
            {showAttribution && (
              <footer className="mt-8 text-[11px] uppercase tracking-[0.35em] text-amber-400 font-bold">
                — {attribution}
              </footer>
            )}
          </blockquote>
        </FadeIn>
      </Container>
    </section>
  );
}
